import { useEffect, useState } from 'react';
import { AuthProvider, useAuth, LoginPage } from 'auth-sso-kit/client/index.js';
import { ThemeProvider } from './context/ThemeContext';
import { AppShell } from './components/layout/AppShell';
import { TableauPage } from './pages/TableauPage';
import { ParticipantsPage } from './pages/ParticipantsPage';
import { ParticipantPage } from './pages/ParticipantPage';
import { FacturesPage } from './pages/FacturesPage';
import { FactureImprimerPage } from './pages/FactureImprimerPage';
import { AdminPage } from './pages/AdminPage';
import { api } from './api/client';

// Routage par hash : '#participants/2022', '#factures/3/imprimer'.
export type Page = 'tableau' | 'participants' | 'factures' | 'admin';
const PAGES: Page[] = ['tableau', 'participants', 'factures', 'admin'];

interface RouteState { page: Page; id: number | null; action: string | null }

function parseHash(): RouteState {
  const [raw, rawId, action] = (window.location.hash.slice(1) || 'tableau').split('/');
  const page = (PAGES as string[]).includes(raw) ? (raw as Page) : 'tableau';
  return { page, id: rawId ? Number(rawId) || null : null, action: action ?? null };
}

export function naviguer(page: Page, id?: number, action?: string) {
  window.location.hash = [page, id, action].filter((x) => x != null).join('/');
}

function AppContent() {
  const { user, loading, isAdmin } = useAuth();
  const [route, setRoute] = useState<RouteState>(parseHash);
  const [eveilDisponible, setEveilDisponible] = useState(true);

  useEffect(() => {
    const onHash = () => {
      const r = parseHash();
      if (r.page === 'admin' && !isAdmin) { window.location.hash = 'tableau'; return; }
      setRoute(r);
    };
    onHash();
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [isAdmin]);

  useEffect(() => {
    if (!user) return;
    api.getMeta().then((m) => setEveilDisponible(m.eveil_disponible)).catch(() => setEveilDisponible(false));
  }, [user]);

  if (loading) return <div className="login-page"><div className="spinner" /></div>;
  if (!user) {
    return <LoginPage title="Encaissements SISP" subtitle="Suivi des paiements et soldes des participant·e·s SISP" emailPlaceholder="prenom.nom@aeveil.ch" />;
  }

  // Le relevé imprimable se passe du cadre (topbar/sidebar).
  if (route.page === 'factures' && route.id != null && route.action === 'imprimer') {
    return <FactureImprimerPage id={route.id} />;
  }

  const rendu = () => {
    switch (route.page) {
      case 'participants': return route.id != null ? <ParticipantPage id={route.id} /> : <ParticipantsPage />;
      case 'factures': return <FacturesPage />;
      case 'admin': return <AdminPage />;
      default: return <TableauPage />;
    }
  };

  return (
    <ThemeProvider>
      <AppShell page={route.page} onNavigate={naviguer} eveilDisponible={eveilDisponible}>
        {rendu()}
      </AppShell>
    </ThemeProvider>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
